// /inbox — versión data-mining (S1.2c+).
// Lista conversations del tenant + transcript. Permite filtrar por fecha
// (última actividad) y eliminar conversaciones de prueba/irrelevantes
// (cascade borra messages; no toca el lead).

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import {
  MessageSquare,
  Globe,
  RefreshCw,
  Bot,
  User,
  UserCog,
  ArrowUpRight,
  Trash2,
  ChevronLeft,
} from 'lucide-react';
import {
  useConversationsList,
  useConversationMessages,
  useConversationLastMessages,
  useDeleteConversation,
  type Conversation,
  type Message,
} from '~/lib/conversations-queries';
import { personDisplayName, toTitleCaseName, transportLabel } from '~/lib/display-name';
import { prettyPhone } from '~/lib/phone';
import { useActiveTenantId, useTenantContext } from '~/lib/tenantContext';
import { currentUser } from '~/lib/pb';
import { can } from '~/lib/permissions';
import { resolvePeriod, tenantTimeZone, type PeriodPreset } from '~/lib/dashboard-period';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { formatPbError } from '~/lib/pb-error';
import { stripMd } from '~/lib/strip-md';
import styles from './_authed.inbox.module.css';

export const Route = createFileRoute('/_authed/inbox')({
  component: InboxPage,
});

type DateFilterKey = 'all' | PeriodPreset;

const DATE_FILTERS: { key: DateFilterKey; label: string }[] = [
  { key: 'all', label: 'Todas' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
];

function formatRelative(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `hace ${days}d`;
  return new Intl.DateTimeFormat('es-CL', {
    day: '2-digit',
    month: 'short',
  }).format(d);
}

function shortenId(id: string): string {
  if (id.startsWith('v_')) return id.slice(0, 8) + '…';
  return id;
}

function conversationLabel(conv: Conversation): string {
  const contact = conv.expand?.contact;
  if (contact) {
    const name = personDisplayName({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
    });
    if (name) return name;
    if (contact.phone) return prettyPhone(contact.phone);
  }
  return 'Visitante';
}

function conversationSecondary(conv: Conversation): string {
  return `${transportLabel(conv.transport)} · ${shortenId(conv.visitor_id)}`;
}

function InboxPage() {
  const tenantId = useActiveTenantId();
  const { selectedTenant } = useTenantContext();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilterKey>('all');
  const canDelete = can(currentUser()?.role, 'deleteRecords');

  const range = useMemo(() => {
    if (dateFilter === 'all') return { from: null as string | null, to: null as string | null };
    const r = resolvePeriod({ key: dateFilter }, { timeZone: tenantTimeZone(selectedTenant) });
    return { from: r.start, to: r.end };
  }, [dateFilter, selectedTenant]);

  const { data, isLoading, isError, refetch, isFetching } = useConversationsList({
    transport: 'all',
    from: range.from,
    to: range.to,
    perPage: 100,
  });
  const conversations = data?.items ?? [];
  const total = data?.totalItems ?? 0;

  // Si el filtro deja fuera la selección actual, la limpiamos.
  useEffect(() => {
    if (selectedId && !conversations.some((c) => c.id === selectedId)) {
      setSelectedId(null);
    }
  }, [conversations, selectedId]);

  const selected = conversations.find((c) => c.id === selectedId) ?? null;
  const previewMap = useConversationLastMessages(conversations.map((c) => c.id)).data ?? {};

  if (!tenantId) {
    return (
      <div className="empty-state">Sin tenant activo. Selecciona uno desde el switcher arriba.</div>
    );
  }

  return (
    <div className={styles.page}>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="h2">Mensajes</h1>
          <p className="page-header__subtitle">
            {total === 1 ? '1 conversación' : `${total} conversaciones`}
          </p>
        </div>
        <div className="page-header__actions">
          <div className={styles.filters} role="group" aria-label="Filtrar por fecha">
            {DATE_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`${styles.filter} ${dateFilter === f.key ? styles.filter__active : ''}`}
                onClick={() => setDateFilter(f.key)}
                aria-pressed={dateFilter === f.key}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label="Refrescar"
            title="Refrescar"
          >
            <RefreshCw size={14} className={isFetching ? styles.spin : ''} />
            Refrescar
          </button>
        </div>
      </header>

      <div
        className={`${styles.split}${selected ? ` ${styles.splitDetail}` : ''}${
          !isLoading && !isError && conversations.length === 0 ? ` ${styles.splitEmpty}` : ''
        }`}
      >
        <aside className={styles.list} aria-label="Lista de conversaciones">
          {isLoading && <div className={styles.listEmpty}>Cargando…</div>}
          {!isLoading && isError && (
            <div className={styles.listEmpty}>
              No pudimos cargar las conversaciones. Recarga o intenta más tarde.
            </div>
          )}
          {!isLoading && !isError && conversations.length === 0 && (
            <div className={styles.listEmpty}>
              {dateFilter === 'all'
                ? 'Aún no hay conversaciones. Cuando alguien escriba al bot del sitio, aparecerá aquí.'
                : 'Ninguna conversación en este período.'}
            </div>
          )}
          {conversations.map((c) => (
            <ConversationItem
              key={c.id}
              conv={c}
              selected={c.id === selectedId}
              preview={previewMap[c.id]}
              onClick={() => setSelectedId(c.id)}
              canDelete={canDelete}
              onDeleted={() => {
                if (selectedId === c.id) setSelectedId(null);
              }}
            />
          ))}
        </aside>

        <section className={styles.transcript} aria-label="Transcripción">
          {selected ? (
            <Transcript
              conv={selected}
              canDelete={canDelete}
              onBack={() => setSelectedId(null)}
              onDeleted={() => setSelectedId(null)}
            />
          ) : (
            <div className={styles.transcriptEmpty}>
              <MessageSquare size={32} aria-hidden />
              <p>Selecciona una conversación para ver el transcript.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ConversationItem({
  conv,
  selected,
  preview,
  onClick,
  canDelete,
  onDeleted,
}: {
  conv: Conversation;
  selected: boolean;
  preview?: string;
  onClick: () => void;
  canDelete: boolean;
  onDeleted: () => void;
}) {
  const Icon = conv.transport === 'whatsapp' ? MessageSquare : Globe;
  const previewText = preview ? stripMd(preview).replace(/\s+/g, ' ').trim() : '';
  return (
    <div className={`${styles.item} ${selected ? styles.item__selected : ''}`}>
      <button type="button" className={styles.item__main} onClick={onClick}>
        <span className={styles.item__icon} aria-hidden>
          <Icon size={14} />
        </span>
        <span className={styles.item__body}>
          <span className={styles.item__head}>
            <span className={styles.item__visitor}>{conversationLabel(conv)}</span>
            <span className={styles.item__time}>{formatRelative(conv.last_message_at)}</span>
          </span>
          {previewText ? (
            <span className={styles.item__preview} title={previewText}>
              {previewText}
            </span>
          ) : null}
          <span className={styles.item__meta}>
            <span className={styles.item__transport}>{conversationSecondary(conv)}</span>
            {conv.expand?.lead && <span className={styles.item__lead}>oportunidad</span>}
            {conv.status !== 'active' && <span className={styles.item__status}>{conv.status}</span>}
            {conv.bot_paused && <span className={styles.item__paused}>bot pausado</span>}
          </span>
        </span>
      </button>
      {canDelete && (
        <DeleteConversationButton
          conv={conv}
          className={styles.item__delete}
          onDeleted={onDeleted}
          compact
        />
      )}
    </div>
  );
}

function Transcript({
  conv,
  canDelete,
  onBack,
  onDeleted,
}: {
  conv: Conversation;
  canDelete: boolean;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const { data: messages = [], isLoading } = useConversationMessages(conv.id);

  return (
    <>
      <header className={styles.transcript__head}>
        <div className={styles.transcript__titles}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={onBack}
            aria-label="Volver a la lista"
          >
            <ChevronLeft size={18} aria-hidden />
            <span>Volver</span>
          </button>
          <h2 className={styles.transcript__title}>{conversationLabel(conv)}</h2>
          <p className={styles.transcript__sub}>
            {conv.expand?.lead?.title && (
              <>
                <span>Oportunidad: {toTitleCaseName(conv.expand.lead.title)}</span>
                {' · '}
              </>
            )}
            {conversationSecondary(conv)} · {conv.status}
            {conv.bot_paused ? ' · bot pausado' : ''}
            {' · '}
            {messages.length === 1 ? '1 mensaje' : `${messages.length} mensajes`}
          </p>
        </div>
        <div className={styles.transcript__actions}>
          {conv.expand?.lead?.id && (
            <Link
              to="/oportunidades/$leadId"
              params={{ leadId: conv.expand.lead.id }}
              className={styles.transcript__leadLink}
            >
              Ver oportunidad
              <ArrowUpRight size={14} aria-hidden />
            </Link>
          )}
          {canDelete && <DeleteConversationButton conv={conv} onDeleted={onDeleted} />}
        </div>
      </header>

      <div className={styles.transcript__body}>
        {isLoading && <p className={styles.transcript__loading}>Cargando transcript…</p>}
        {!isLoading && messages.length === 0 && (
          <p className={styles.transcript__loading}>Sin mensajes en esta conversación.</p>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} msg={m} />
        ))}
      </div>
    </>
  );
}

function DeleteConversationButton({
  conv,
  onDeleted,
  compact = false,
  className,
}: {
  conv: Conversation;
  onDeleted: () => void;
  compact?: boolean;
  className?: string;
}) {
  const confirm = useConfirm();
  const del = useDeleteConversation();
  const [error, setError] = useState<string | null>(null);

  async function handleDelete(e: MouseEvent) {
    e.stopPropagation();
    setError(null);
    const label = conversationLabel(conv);
    const ok = await confirm({
      title: 'Eliminar conversación',
      description: `Se borrará el chat con «${label}» y todos sus mensajes. La oportunidad (si existe) no se toca.`,
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await del.mutateAsync(conv.id);
      onDeleted();
    } catch (err) {
      setError(formatPbError(err) || 'No pudimos eliminar.');
    }
  }

  return (
    <span className={className}>
      <button
        type="button"
        className={compact ? styles.deleteIconBtn : 'btn btn--ghost btn--sm'}
        onClick={handleDelete}
        disabled={del.isPending}
        aria-label="Eliminar conversación"
        title="Eliminar conversación"
      >
        <Trash2 size={14} aria-hidden />
        {!compact && (del.isPending ? 'Eliminando…' : 'Eliminar')}
      </button>
      {error && !compact && (
        <span className={styles.deleteError} role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

function MessageBubble({ msg }: { msg: Message }) {
  const RoleIcon = msg.role === 'visitor' ? User : msg.role === 'bot' ? Bot : UserCog;
  const date = new Date(msg.occurred_at);
  const time = new Intl.DateTimeFormat('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return (
    <div className={`${styles.bubble} ${styles[`bubble__${msg.role}`]}`}>
      <div className={styles.bubble__head}>
        <RoleIcon size={12} aria-hidden />
        <span className={styles.bubble__role}>{msg.role}</span>
        <span className={styles.bubble__time}>{time}</span>
      </div>
      <div className={styles.bubble__content}>{stripMd(msg.content)}</div>
      {msg.role === 'bot' && msg.tokens_out !== undefined && (
        <div className={styles.bubble__meta}>
          {msg.tokens_in ?? 0} in · {msg.tokens_out ?? 0} out
          {msg.cost_usd !== undefined ? ` · $${msg.cost_usd.toFixed(4)}` : ''}
        </div>
      )}
    </div>
  );
}
